/**
 * @copyright Microsoft Corporation. All rights reserved.
 *
 * @file FileCard social bar component
 */

import * as React from 'react';
import { FocusZone, FocusZoneDirection } from 'office-ui-fabric-react/lib/FocusZone';
import { ActivityItem } from 'office-ui-fabric-react/lib/components/ActivityItem/index';
import { css } from '@uifabric/utilities/lib/css';
import {
  analyticsItemFacet,
  likedByPeopleFacet,
  likedByItemFacet
} from '../../../facets/ItemFacets';
import {
  likeCountFacet
} from '../../../facets/ItemAnalyticsFacets';
import { List } from 'office-ui-fabric-react/lib/List';
import { Panel, PanelType, IPanelProps } from 'office-ui-fabric-react/lib/Panel';
import { IWizardActionStateProps } from '../../wizardAction/WizardAction';
import { WithItemCache, IItemCacheStateProps } from '../../withItemCache/WithItemCache';
import { userHoverCardControl } from '../../../controls/userHoverCardControl';
import { displayNameFacet } from '../../../facets/ItemFacets';
import { emailFacet } from '../../../facets/PersonFacets';
import { format, getLocalizedCountValue } from '@ms/utilities-strings/lib/index';

import strings from './SocialBar.resx';
import * as styles from './SocialBar.scss';

export interface ISocialBarPanelProps extends IWizardActionStateProps, IPanelProps {
  itemKey: string | undefined;
  onRenderUser?: () => void;
}

export interface IPersonaListProps {
  peopleList?: Array<string>;
}

export default class SocialBarPanel extends React.Component<ISocialBarPanelProps, {}> {
  public render(): React.ReactElement<{}> {
    const { itemKey, resolve } = this.props;
    return (
      <WithItemCache>{ ({ itemCache }: IItemCacheStateProps): JSX.Element => {
        const analyticsKey = itemCache.demandItemFacet(analyticsItemFacet, itemKey);
        const itemAnalyticsKey = analyticsKey && analyticsKey.itemKey;
        const likeCount = itemCache.demandItemFacet(likeCountFacet, itemAnalyticsKey);
        const likeCountString = (count: number) => format(getLocalizedCountValue(
          strings.panelLikesHeaderText,
          strings.panelLikesHeaderTextInterval,
          count) || '',
          count);

        return (
          <Panel
            className={ styles.socialBarPanel }
            closeButtonAriaLabel={ strings.panelCloseAriaLabel }
            customWidth='320px'
            isOpen={ true }
            isBlocking={ true }
            isLightDismiss={ true }
            focusTrapZoneProps={ {
              forceFocusInsideTrap: false,
              isClickableOutsideFocusTrap: true
            } }
            type={ PanelType.custom }
            onDismissed={ resolve }
            headerText={ `${likeCountString(Number(likeCount))}` }
          >
            { panelBody(this.props) }
          </Panel>);
      }
      }</WithItemCache>
    );
  }
}

function panelBody(props: ISocialBarPanelProps): React.ReactElement<{}> {
  return (
    <WithItemCache>{ ({ itemCache }: IItemCacheStateProps): JSX.Element => {
      const likedByItem = itemCache.demandItemFacet(likedByItemFacet, props.itemKey);
      const personList = itemCache.demandItemFacet(likedByPeopleFacet, likedByItem && likedByItem.likedByItemKey);
      const personKeys = personList && personList.personKeys;

      return (
        <div className={ css(styles.socialBarPanelContent) }>
          { personKeys && personKeys.length > 0 && getLikedByList(personKeys) }
        </div>
      );
    } }
    </WithItemCache>
  );
}

function getLikedByList(personKeys?: string[]): React.ReactElement<{}> {
  return (
    <FocusZone direction={ FocusZoneDirection.vertical }>
      <List
        className={ styles.socialBarList }
        items={ personKeys && personKeys.map((personKey: string): {} => {
          return {
            key: personKey
          };
        }) }
        onRenderCell={ onRenderUser }
      />
    </FocusZone>
  );
}

function onRenderUser(item?: { key: string }, index?: number, isScrolling?: boolean): React.ReactNode {
  return item && (
    <WithItemCache>{ ({ itemCache }: IItemCacheStateProps): JSX.Element => {
      const personKey = item && item.key;
      const controlHandler = itemCache.demandItemFacet(userHoverCardControl, personKey);
      const displayName = itemCache.demandItemFacet(displayNameFacet, personKey);
      const email = itemCache.demandItemFacet(emailFacet, personKey);

      const {
        userHoverCard
      } = userHoverCardControl.evaluate(controlHandler)(itemCache, {
        personKey
      });
      const UserHoverCard = userHoverCard;

      const personaElement = <ActivityItem
        className={ styles.socialBarPanelPersona }
        activityPersonas={ [{ imageUrl: email, imageAlt: displayName }] }
        activityDescription={
          <div className={ styles.socialBarPanelPersonaDescription }>
            { displayName }
          </div>
        }
      />;

      return UserHoverCard ? (
        <UserHoverCard personKey={ personKey } key={ index }>
          { personaElement }
        </ UserHoverCard>
      ) :
        personaElement;
    } }</WithItemCache>
  );
}